import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Button } from '@mailflow/ui';
import {
  suppressionsQuery,
  useCreateSuppression,
  useImportSuppressions,
  useDeleteSuppression,
  type Suppression,
  type SuppressionScope,
} from '../lib/suppressions.js';

/**
 * Die Sperrliste — sichtbar gemacht.
 *
 * Die Regeln greifen vor jeder einzelnen Nachricht. Solange man sie nicht
 * sehen kann, wirkt jede nicht zugestellte Mail wie ein Fehler des Systems.
 * Diese Seite beantwortet genau eine Frage: warum bekommt diese Adresse keine
 * Post — und seit wann.
 */

const SCOPE_LABELS: Record<SuppressionScope, string> = {
  email: 'Adresse',
  domain: 'Domain',
  pattern: 'Muster',
  hash: 'Gelöscht (Hash)',
};

const REASON_LABELS: Record<string, string> = {
  manual: '1. Manuell',
  unsubscribe: '2. Abgemeldet',
  no_interest: '3. Kein Interesse',
  bounce: 'Unzustellbar',
  complaint: 'Beschwerde',
  import: 'Übernommen',
};

export function SuppressionsPage() {
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('alle');
  const [value, setValue] = useState('');
  const [scope, setScope] = useState<SuppressionScope>('email');
  const [reason, setReason] = useState('manual');
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const entries = useQuery({ ...suppressionsQuery(search), placeholderData: keepPreviousData });
  const create = useCreateSuppression();
  const importList = useImportSuppressions();
  const remove = useDeleteSuppression();

  const rawItems = entries.data?.items ?? [];
  const items = rawItems.filter((item) => {
    if (reasonFilter === 'alle') return true;
    if (reasonFilter === 'manual') return item.reason === 'manual' || item.reason === 'import';
    return item.reason === reasonFilter;
  });
  const fehler = [create.error, importList.error, remove.error].find(Boolean);

  return (
    <div className="stack" style={{ gap: 'var(--space-6)' }}>
      <nav className="breadcrumb text-sm">
        <Link to="/kontakte">Kontakte</Link>
        <span aria-hidden="true"> / </span>
        <span className="muted">Sperrliste</span>
      </nav>

      <header className="page-header">
        <div>
          <h1 className="text-2xl">Sperrliste</h1>
          <p className="muted text-sm">
            Diese Regeln werden vor jeder einzelnen Nachricht geprüft — nicht nur beim Start einer
            Kampagne. Wer hier steht, bekommt keine Post, auch nicht über einen späteren Import.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setImportOpen((open) => !open)}>
          Liste übernehmen
        </Button>
      </header>

      {fehler && (
        <div className="alert" data-tone="danger" role="alert">
          {fehler instanceof Error ? fehler.message : 'Die Aktion ist fehlgeschlagen.'}
        </div>
      )}

      {notice && (
        <div className="alert" data-tone="success" role="status">
          {notice}
        </div>
      )}

      {importOpen && (
        <section className="card">
          <div className="card__body stack">
            <h2 className="text-lg">Bestehende Sperrliste übernehmen</h2>
            <p className="muted text-sm">
              Aus Sendy, Mailchimp oder einer Textdatei: eine Regel je Zeile, Kommas und Semikola
              gehen auch. Die Art wird je Zeile erkannt — <code>max@firma.ch</code> als Adresse,{' '}
              <code>firma.ch</code> als Domain, <code>@firma.ch</code> oder <code>info@*</code> als
              Muster. Was nicht lesbar ist, wird zurückgemeldet statt still verworfen.
            </p>
            <textarea
              className="input"
              rows={8}
              value={importText}
              spellCheck={false}
              placeholder={'max@firma.ch\nkonkurrent.ch\n@spam-domain.tld'}
              aria-label="Sperrliste einfügen"
              onChange={(e) => setImportText(e.target.value)}
              style={{ fontFamily: 'var(--font-mono, monospace)', resize: 'vertical' }}
            />
            <div className="dialog__actions">
              <Button variant="secondary" onClick={() => setImportOpen(false)}>
                Abbrechen
              </Button>
              <Button
                onClick={() => void uebernehmen()}
                disabled={importText.trim().length === 0}
                loading={importList.isPending}
              >
                Übernehmen
              </Button>
            </div>

            {importList.data && importList.data.invalid.length > 0 && (
              <div className="alert" data-tone="warning" role="alert">
                <strong>{importList.data.invalid.length} Zeilen waren nicht lesbar:</strong>{' '}
                {importList.data.invalid.slice(0, 8).join(', ')}
                {importList.data.invalid.length > 8 && ' …'}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="card">
        <div className="card__body stack">
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Eintrag hinzufügen</h2>
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <input
              className="input"
              style={{ flex: 2, minWidth: '220px' }}
              value={value}
              maxLength={320}
              placeholder="max@firma.ch, firma.ch oder @firma.ch"
              aria-label="Adresse, Domain oder Muster"
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void hinzufuegen();
                }
              }}
            />
            <select
              className="input"
              style={{ maxWidth: '160px' }}
              value={scope}
              onChange={(e) => setScope(e.target.value as SuppressionScope)}
              aria-label="Art der Regel"
            >
              <option value="email">Adresse</option>
              <option value="domain">Domain</option>
              <option value="pattern">Muster</option>
            </select>
            <select
              className="input"
              style={{ maxWidth: '200px' }}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              aria-label="Grund"
            >
              <option value="manual">1. Manuell (Intern)</option>
              <option value="unsubscribe">2. Abgemeldet (Unsubscribe)</option>
              <option value="no_interest">3. Kein Interesse (Opt-Out)</option>
              <option value="bounce">Unzustellbar</option>
              <option value="complaint">Beschwerde</option>
            </select>
            <Button
              onClick={() => void hinzufuegen()}
              disabled={value.trim().length === 0}
              loading={create.isPending}
            >
              Sperren
            </Button>
          </div>
          <p className="field__hint">
            <code>*.firma.ch</code> als Domain trifft zusätzlich alle Subdomains.
          </p>
        </div>
      </section>

      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 'var(--space-3)' }}>
        <input
          className="input"
          type="search"
          placeholder="Adresse oder Domain suchen …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: '320px' }}
          aria-label="Sperrliste durchsuchen"
        />

        <div className="tabs" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            type="button"
            className={`button ${reasonFilter === 'alle' ? '' : 'button--secondary'}`}
            style={{ padding: '4px 12px', fontSize: 'var(--text-sm)' }}
            onClick={() => setReasonFilter('alle')}
          >
            Alle
          </button>
          <button
            type="button"
            className={`button ${reasonFilter === 'manual' ? '' : 'button--secondary'}`}
            style={{ padding: '4px 12px', fontSize: 'var(--text-sm)' }}
            onClick={() => setReasonFilter('manual')}
          >
            1. Manuell
          </button>
          <button
            type="button"
            className={`button ${reasonFilter === 'unsubscribe' ? '' : 'button--secondary'}`}
            style={{ padding: '4px 12px', fontSize: 'var(--text-sm)' }}
            onClick={() => setReasonFilter('unsubscribe')}
          >
            2. Abgemeldet
          </button>
          <button
            type="button"
            className={`button ${reasonFilter === 'no_interest' ? '' : 'button--secondary'}`}
            style={{ padding: '4px 12px', fontSize: 'var(--text-sm)' }}
            onClick={() => setReasonFilter('no_interest')}
          >
            3. Kein Interesse
          </button>
        </div>

        <span className="muted text-sm" data-numeric>
          {items.length >= 500 ? 'über 500 Einträge' : `${items.length} Einträge`}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="card">
          <div className="card__body stack" style={{ alignItems: 'flex-start' }}>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>
              {search ? 'Kein Treffer' : 'Noch nichts gesperrt'}
            </h2>
            <p className="muted text-sm">
              {search
                ? 'Zu dieser Suche gibt es keinen Eintrag — die Adresse ist also nicht gesperrt.'
                : 'Abmeldungen, harte Bounces und Beschwerden landen automatisch hier. Eine bestehende Liste kannst du oben übernehmen.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Regel</th>
                  <th scope="col">Art</th>
                  <th scope="col">Grund</th>
                  <th scope="col">Seit</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {items.map((entry) => (
                  <tr key={entry.id}>
                    <td style={{ fontFamily: 'var(--font-mono, monospace)' }}>{entry.value}</td>
                    <td>{SCOPE_LABELS[entry.scope]}</td>
                    <td>
                      <span className="badge">{REASON_LABELS[entry.reason] ?? entry.reason}</span>
                      {/* Herkunft nur nennen, wenn sie etwas hinzufügt — bei
                          reason=import stünde sonst zweimal dasselbe. */}
                      {entry.source === 'import' && entry.reason !== 'import' && (
                        <span className="muted text-sm"> · übernommen</span>
                      )}
                    </td>
                    <td className="muted text-sm">
                      {new Date(entry.createdAt).toLocaleDateString('de-CH')}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {confirmId === entry.id ? (
                        <>
                          <span className="text-sm" style={{ marginRight: 'var(--space-2)' }}>
                            Sperre aufheben? Diese Adresse bekommt danach wieder Post.
                          </span>
                          <Button variant="danger" onClick={() => void aufheben(entry)}>
                            Aufheben
                          </Button>{' '}
                          <Button variant="secondary" onClick={() => setConfirmId(null)}>
                            Abbrechen
                          </Button>
                        </>
                      ) : (
                        <Button variant="ghost" onClick={() => setConfirmId(entry.id)}>
                          Sperre aufheben
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  async function hinzufuegen() {
    const wert = value.trim();
    if (wert.length === 0) return;
    await create.mutateAsync({ scope, value: wert, reason });
    setValue('');
    setNotice(`„${wert}" ist gesperrt.`);
  }

  async function uebernehmen() {
    const result = await importList.mutateAsync({ text: importText, reason: 'import' });
    setNotice(
      `${result.created} Regeln übernommen.` +
        (result.duplicates > 0 ? ` ${result.duplicates} waren schon gesperrt.` : ''),
    );
    if (result.invalid.length === 0) {
      setImportText('');
      setImportOpen(false);
    }
  }

  async function aufheben(entry: Suppression) {
    await remove.mutateAsync(entry.id);
    setConfirmId(null);
    setNotice(`Die Sperre für „${entry.value}" ist aufgehoben.`);
  }
}
